import { useQuery } from "@apollo/client";
import { useForm } from "react-hook-form";

import CodeEditor from "components/CodeEditor";
import FlashContext from "components/FlashMessages/FlashContext";
import { External } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import SecretInput from "ds/components/SecretInput";
import Typography from "ds/components/Typography";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";
import { stringIsRequired } from "utils/formValidators";
import DrawerForm from "ds/components/DrawerNew/Form";

import { SettingsSlackAppConfig } from "../gql";
import useConnectSlackIntegration from "../useConnectSlackIntegration";
import { MANIFEST_TEMPLATE } from "./constants";
import { GET_SLACK_APP_MANIFEST, SELF_HOSTED_SLACK_APP_CONFIG_SET } from "./gql";
import { getFormDefaultValues, SettingsIntegrationsSlackSetUpFormFields } from "./helpers";

const useSlackAppConfigSet = createDefaultMutationHook(SELF_HOSTED_SLACK_APP_CONFIG_SET);

type SettingsIntegrationsSlackSetUpProps = {
  appConfig?: SettingsSlackAppConfig;
};

const SettingsIntegrationsSlackSetUp = createDrawer(function SettingsIntegrationsSlackSetUp({
  appConfig,
}: SettingsIntegrationsSlackSetUpProps) {
  const isEditMode = !!appConfig;
  const { reportSuccess } = useTypedContext(FlashContext);

  const { data, loading: manifestLoading } = useQuery(GET_SLACK_APP_MANIFEST, {
    fetchPolicy: "cache-first",
  });

  const [slackAppConfigSet, { loading }] = useSlackAppConfigSet({
    refetchQueries: ["GetAccountIntegrations"],
  });
  const { connectSlackIntegration } = useConnectSlackIntegration();

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm<SettingsIntegrationsSlackSetUpFormFields>({
    mode: "onChange",
    defaultValues: getFormDefaultValues(appConfig),
  });

  const onSubmit = handleSubmit((data) => {
    slackAppConfigSet(
      {
        input: {
          clientId: data.clientId,
          clientSecret: data.clientSecret,
          signingSecret: data.signingSecret,
        },
      },
      (data) => {
        if (data?.slackAppConfigSet.clientId) {
          connectSlackIntegration();
          reportSuccess({
            message: isEditMode
              ? "Slack application successfully updated"
              : "Slack application successfully created",
          });
        }
      }
    );
  });

  const editorBody = data?.slackAppManifest || MANIFEST_TEMPLATE;

  return (
    <DrawerForm isDirty={isDirty} isDismissable={false}>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle
          title={isEditMode ? "Edit Slack integration" : "Set up Slack integration"}
        />
        <DrawerCloseIcon />
      </DrawerHeader>

      {!isEditMode && (
        <DrawerBody hasDivider gap="x-large">
          <Typography variant="p-t5" tag="h3">
            Create a Slack app
          </Typography>

          <FormField label="Copy app manifest" withCopy={editorBody} noMargin>
            <CodeEditor
              body={editorBody}
              language="json"
              skeletonCount={6}
              fixedHeight={180}
              readOnly={manifestLoading}
              rounded
            />
          </FormField>

          <Box>
            <LinkButton
              href="https://api.slack.com/apps?new_app=1"
              target="_blank"
              variant="secondary"
              size="medium"
              endIcon={External}
            >
              Create Slack app
            </LinkButton>
          </Box>
        </DrawerBody>
      )}

      <form onSubmit={onSubmit}>
        <DrawerBody gap="x-large">
          <FormField label="Client ID" error={errors?.clientId?.message} noMargin>
            {({ ariaInputProps }) => (
              <Input
                error={!!errors?.clientId}
                {...register("clientId", {
                  validate: stringIsRequired(),
                  setValueAs: (value: string) => value.trim(),
                })}
                {...ariaInputProps}
                disabled={isEditMode}
              />
            )}
          </FormField>

          <FormField label="Client Secret" error={errors?.clientSecret?.message} noMargin>
            {({ ariaInputProps }) => (
              <SecretInput
                error={!!errors?.clientSecret}
                {...register("clientSecret", {
                  validate: stringIsRequired(),
                  setValueAs: (value: string) => value.trim(),
                })}
                {...ariaInputProps}
              />
            )}
          </FormField>

          <FormField label="Signing Secret" error={errors?.signingSecret?.message} noMargin>
            {({ ariaInputProps }) => (
              <SecretInput
                error={!!errors?.signingSecret}
                {...register("signingSecret", {
                  validate: stringIsRequired(),
                  setValueAs: (value: string) => value.trim(),
                })}
                {...ariaInputProps}
              />
            )}
          </FormField>
        </DrawerBody>

        <DrawerFooter fullWidth>
          <DrawerFooterActions>
            <DrawerCancelButton />
            <Button variant="primary" type="submit" loading={loading}>
              {isEditMode ? "Save" : "Save & Connect"}
            </Button>
          </DrawerFooterActions>
        </DrawerFooter>
      </form>
    </DrawerForm>
  );
});

export const openSetUpDrawer = createDrawerTrigger(SettingsIntegrationsSlackSetUp);
